import { MultiScreenController } from '../controllers/MultiScreenController';
import { OIDCTokenSet } from 'infrastructure/oidc/OIDCClient';
import { Project } from 'core/models/project';
import { Vehicle } from 'core/models/vehicle';
import { SubScreenLayout } from 'core/models/layout';
import { MultiScreenMessage } from './MultiScreenMessage';
import { Environment } from 'core/models/environment';
import { MultiScreenSession } from './MultiScreenSession';

export class MultiScreenServer {
    private controller: MultiScreenController;

    private _tokenSet: OIDCTokenSet | null = null;
    private _selectedProject: Project | null = null;
    private _selectedEnvironment: Environment | null = null;
    private _vehicleId: Vehicle.Id | null = null;
    private _cameraLayouts: SubScreenLayout[] | null = null;
    private _numberOfScreens = 0;

    constructor(controller: MultiScreenController) {
        this.controller = controller;

        controller.onMessage = (session: MultiScreenSession | undefined, msg: string | Blob | ArrayLike<unknown>) => {
            const json = JSON.parse(msg as string) as MultiScreenMessage;
            switch (json.type) {
                case 'Hello':
                    this._sendTokenSetIfAny(session);
                    this._sendSelectedProjectIfAny(session);
                    this._sendSelectedEnvironmentIfAny(session);
                    this._sendVehicleIdIfAny(session);
                    this._sendCameraLayoutsIfAny(session);
                    this._sendNumberOfScreensIfAny(session);
                    break;
                default:
                    console.warn(`message through ${session?.presentationId}`, msg);
                    break;
            }
        };

        controller.onSession = () => {
            this._numberOfScreens = controller.sessions.size;
            this._sendNumberOfScreensIfAny();
        };
    }

    private _sendMessage(message: MultiScreenMessage, session: MultiScreenSession | undefined) {
        const data = JSON.stringify(message);
        if (session) {
            session.send(data); // intentionally not awaiting
        } else {
            this.controller.sendAll(data); // intentionally not awaiting
        }
    }

    set tokenSet(value: OIDCTokenSet | null) {
        this._tokenSet = value;
        if (this.controller.hasSession) {
            this._sendTokenSetIfAny();
        }
    }

    private _sendTokenSetIfAny(session?: MultiScreenSession) {
        const tokenSet = this._tokenSet;
        if (!tokenSet) return;

        this._sendMessage({
            type: 'TokenSet',
            tokenSet,
        }, session);
    }

    set selectedProject(value: Project | null) {
        this._selectedProject = value;
        if (this.controller.hasSession) {
            this._sendSelectedProjectIfAny();
        }
    }

    private _sendSelectedProjectIfAny(session?: MultiScreenSession) {
        const selectedProject = this._selectedProject;
        this._sendMessage({
            type: 'Project',
            selectedProject,
        }, session);
    }

    set selectedEnvironment(value: Project | null) {
        this._selectedEnvironment = value;
        if (this.controller.hasSession) {
            this._sendSelectedEnvironmentIfAny();
        }
    }

    private _sendSelectedEnvironmentIfAny(session?: MultiScreenSession) {
        const selectedEnvironment = this._selectedEnvironment;
        this._sendMessage({
            type: 'Environment',
            selectedEnvironment,
        }, session);
    }

    set vehicleId(value: Vehicle.Id | null) {
        this._vehicleId = value;
        if (this.controller.hasSession) {
            this._sendVehicleIdIfAny();
        }
    }

    private _sendVehicleIdIfAny(session?: MultiScreenSession) {
        const vehicleId = this._vehicleId;
        this._sendMessage({
            type: 'VehicleId',
            vehicleId,
        }, session);
    }

    set cameraLayouts(value: SubScreenLayout[] | null) {
        this._cameraLayouts = value;
        if (this.controller.hasSession) {
            this._sendCameraLayoutsIfAny();
        }
    }

    private _sendCameraLayoutsIfAny(session?: MultiScreenSession) {
        const layouts = this._cameraLayouts;
        this._sendMessage({
            type: 'Layouts',
            layouts,
        }, session);
    }

    private _sendNumberOfScreensIfAny(session?: MultiScreenSession) {
        const numberOfScreens = this._numberOfScreens;
        this._sendMessage({
            type: 'NumberOfScreens',
            numberOfScreens,
        }, session);
    }
}
